import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, BuildingIcon, CalendarIcon, CreditCardIcon, FileTextIcon } from 'lucide-react';
import { Settlement } from '../types/Transaction';
export function SettlementDetail() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  // Mock data
  const settlement: Settlement = {
    id: id || '1',
    settlementId: 'SETTLE-2024-001',
    amount: 145000,
    currency: 'LKR',
    status: 'completed',
    date: '2024-01-15T09:00:00',
    payoutDate: '2024-01-17T14:30:00',
    merchantName: 'ABC Store',
    merchantId: 'MERCH-001',
    bankName: 'Genie Business',
    bankAccountNumber: '****1234',
    bankAccountName: 'ABC Store Pvt Ltd',
    branchCode: '001',
    transactionCount: 45,
    transactionIds: ['TXN-2024-001234', 'TXN-2024-001235', 'TXN-2024-001236', 'TXN-2024-001237', 'TXN-2024-001238'],
    fee: 4350,
    netAmount: 140650,
    lifecycle: [{
      id: '1',
      timestamp: '2024-01-15T09:00:00',
      status: 'initiated',
      description: 'Settlement batch created with 45 transactions',
      metadata: {
        transaction_count: '45',
        total_amount: 'Rs. 145,000'
      }
    }, {
      id: '2',
      timestamp: '2024-01-16T10:00:00',
      status: 'processing',
      description: 'Processing payout to merchant bank account',
      metadata: {
        bank: 'Genie Business',
        account: '****1234'
      }
    }, {
      id: '3',
      timestamp: '2024-01-17T14:30:00',
      status: 'completed',
      description: 'Funds successfully transferred to merchant account',
      metadata: {
        reference_number: 'REF123456789',
        net_amount: 'Rs. 140,650'
      }
    }]
  };
  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      initiated: 'bg-blue-100 text-blue-800',
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      reversed: 'bg-gray-100 text-gray-800'
    };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`} data-id="element-5735">
        {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
      </span>;
  };
  return <div className="space-y-6" data-id="element-5736">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-5737">
        <button onClick={() => navigate('/settlements')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-5738">
          <ArrowLeftIcon size={20} data-id="element-5739" />
          Back to Settlements
        </button>
        <div className="flex items-center gap-3" data-id="element-5740">
          <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-5741">
            <DownloadIcon size={18} data-id="element-5742" />
            Download Statement
          </button>
        </div>
      </div>
      {/* Settlement Overview */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5743">
        <div className="flex items-start justify-between mb-6" data-id="element-5744">
          <div data-id="element-5745">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5746">
              {settlement.settlementId}
            </h1>
            <p className="text-gray-500 mt-1" data-id="element-5747">
              Settlement for {settlement.merchantName}
            </p>
          </div>
          {getStatusBadge(settlement.status)}
        </div>
        {/* Amount Summary */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6 pb-6 border-b border-gray-200" data-id="element-5748">
          <div data-id="element-5749">
            <div className="text-sm text-gray-500" data-id="element-5750">Gross Amount</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5751">
              Rs. {settlement.amount.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-5752">
            <div className="text-sm text-gray-500" data-id="element-5753">Processing Fee</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5754">
              Rs. {settlement.fee.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-5755">
            <div className="text-sm text-gray-500" data-id="element-5756">Net Amount</div>
            <div className="mt-1 text-2xl font-semibold text-green-600" data-id="element-5757">
              Rs. {settlement.netAmount.toLocaleString('en-LK')}
            </div>
          </div>
          <div data-id="element-5758">
            <div className="text-sm text-gray-500" data-id="element-5759">Transactions</div>
            <div className="mt-1 text-2xl font-semibold text-gray-900" data-id="element-5760">
              {settlement.transactionCount}
            </div>
          </div>
        </div>
        {/* Settlement Details Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-5761">
          {/* Merchant Information */}
          <div className="space-y-4" data-id="element-5762">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-5763">
              <BuildingIcon size={20} className="text-blue-600" data-id="element-5764" />
              Merchant Information
            </h3>
            <div className="space-y-3" data-id="element-5765">
              <div data-id="element-5766">
                <div className="text-sm text-gray-500" data-id="element-5767">Merchant Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5768">
                  {settlement.merchantName}
                </div>
              </div>
              <div data-id="element-5769">
                <div className="text-sm text-gray-500" data-id="element-5770">Merchant ID</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5771">
                  {settlement.merchantId}
                </div>
              </div>
            </div>
          </div>
          {/* Bank Information */}
          <div className="space-y-4" data-id="element-5772">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-5773">
              <CreditCardIcon size={20} className="text-blue-600" data-id="element-5774" />
              Bank Account Information
            </h3>
            <div className="space-y-3" data-id="element-5775">
              <div data-id="element-5776">
                <div className="text-sm text-gray-500" data-id="element-5777">Bank Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5778">
                  {settlement.bankName}
                </div>
              </div>
              <div data-id="element-5779">
                <div className="text-sm text-gray-500" data-id="element-5780">Account Number</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5781">
                  {settlement.bankAccountNumber}
                </div>
              </div>
              <div data-id="element-5782">
                <div className="text-sm text-gray-500" data-id="element-5783">Account Name</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5784">
                  {settlement.bankAccountName}
                </div>
              </div>
              <div data-id="element-5785">
                <div className="text-sm text-gray-500" data-id="element-5786">Branch Code</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5787">
                  {settlement.branchCode}
                </div>
              </div>
            </div>
          </div>
          {/* Date Information */}
          <div className="space-y-4" data-id="element-5788">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2" data-id="element-5789">
              <CalendarIcon size={20} className="text-blue-600" data-id="element-5790" />
              Timeline
            </h3>
            <div className="space-y-3" data-id="element-5791">
              <div data-id="element-5792">
                <div className="text-sm text-gray-500" data-id="element-5793">Settlement Date</div>
                <div className="text-sm font-medium text-gray-900" data-id="element-5794">
                  {new Date(settlement.date).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                </div>
              </div>
              {settlement.payoutDate && <div data-id="element-5795">
                  <div className="text-sm text-gray-500" data-id="element-5796">Payout Date</div>
                  <div className="text-sm font-medium text-gray-900" data-id="element-5797">
                    {new Date(settlement.payoutDate).toLocaleString('en-GB', {
                  dateStyle: 'long',
                  timeStyle: 'short'
                })}
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
      {/* Included Transactions */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5798">
        <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2" data-id="element-5799">
          <FileTextIcon size={20} className="text-blue-600" data-id="element-5800" />
          Included Transactions ({settlement.transactionCount})
        </h2>
        <div className="space-y-2" data-id="element-5801">
          {settlement.transactionIds.map(txnId => <div key={txnId} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100" data-id="element-5802">
              <span className="text-sm font-medium text-gray-900" data-id="element-5803">{txnId}</span>
              <button onClick={() => navigate(`/sale-detail/${txnId}`)} className="text-sm text-blue-600 hover:text-blue-900" data-id="element-5804">
                View Details
              </button>
            </div>)}
        </div>
      </div>
      {/* Settlement Lifecycle */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-5805">
        <h2 className="text-lg font-medium text-gray-900 mb-6" data-id="element-5806">
          Settlement Lifecycle
        </h2>
        <div className="space-y-6" data-id="element-5807">
          {settlement.lifecycle.map((event, index) => <div key={event.id} className="relative" data-id="element-5808">
              {index !== settlement.lifecycle.length - 1 && <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-gray-200" data-id="element-5809"></div>}
              <div className="flex items-start gap-4" data-id="element-5810">
                <div className="flex-shrink-0" data-id="element-5811">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-5812">
                    <div className="w-3 h-3 bg-blue-600 rounded-full" data-id="element-5813"></div>
                  </div>
                </div>
                <div className="flex-1 min-w-0" data-id="element-5814">
                  <div className="flex items-center justify-between" data-id="element-5815">
                    <div className="flex items-center gap-3" data-id="element-5816">
                      <p className="text-sm font-medium text-gray-900" data-id="element-5817">
                        {event.description}
                      </p>
                      {getStatusBadge(event.status)}
                    </div>
                    <p className="text-sm text-gray-500" data-id="element-5818">
                      {new Date(event.timestamp).toLocaleString('en-GB', {
                    dateStyle: 'short',
                    timeStyle: 'short'
                  })}
                    </p>
                  </div>
                  {event.metadata && <div className="mt-2 p-3 bg-gray-50 rounded-lg" data-id="element-5819">
                      <div className="grid grid-cols-2 gap-2" data-id="element-5820">
                        {Object.entries(event.metadata).map(([key, value]) => <div key={key} data-id="element-5821">
                            <span className="text-xs text-gray-500" data-id="element-5822">
                              {key.replace('_', ' ').toUpperCase()}:
                            </span>
                            <span className="text-xs font-medium text-gray-900 ml-1" data-id="element-5823">
                              {String(value)}
                            </span>
                          </div>)}
                      </div>
                    </div>}
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
}